import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import UserProfile from './components/UserProfile';
import UserProfileView from './components/UserProfileView';
import UserProducts from './components/UserProducts';
import UserJobsServices from './components/UserJobsServices';
import Rentals from './components/Rentals';
import ServiceFees from './components/ServiceFees';
import ServiceFeeEdit from './components/ServiceFeeEdit';
import ServiceFeeAdd from './components/ServiceFeeAdd';
import CustomerFeedback from './components/CustomerFeedback';
import InappropriateFlags from './components/InappropriateFlags';
import AccountDeletionRequests from './components/AccountDeletionRequests';
import MFAEnforcement from './components/MFAEnforcement';
import ResetPassword from './components/ResetPassword';
import MobileAppInstructions from './components/MobileAppInstructions';
import EmailConfirmation from './components/EmailConfirmation';

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <MFAEnforcement>
      {children}
    </MFAEnforcement>
  );
};

// Login Route Handler
const LoginRoute: React.FC = () => {
  const { isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is admin and not currently on login page, redirect to dashboard
  // But if they're on login page, let them complete the MFA flow
  if (isAdmin && location.pathname !== '/') {
    return <Navigate to="/dashboard" replace />;
  }

  // If user is admin and on login page, show login (for MFA completion)
  // If user is not admin, show login
  return <Login />;
};

// Main App component
const AppContent: React.FC = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route 
          path="/" 
          element={<LoginRoute />} 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/user-profile/:userId" 
          element={
            <ProtectedRoute>
              <UserProfile />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/user-profile-view/:userId" 
          element={
            <ProtectedRoute>
              <UserProfileView />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/user-products/:userId" 
          element={
            <ProtectedRoute>
              <UserProducts />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/user-jobs-services/:userId" 
          element={
            <ProtectedRoute>
              <UserJobsServices />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/user-rentals/:userId" 
          element={
            <ProtectedRoute>
              <Rentals />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/service-fees" 
          element={
            <ProtectedRoute>
              <ServiceFees />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/service-fees/add" 
          element={
            <ProtectedRoute>
              <ServiceFeeAdd />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/service-fees/edit" 
          element={
            <ProtectedRoute>
              <ServiceFeeEdit />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/customer-feedback" 
          element={
            <ProtectedRoute>
              <CustomerFeedback />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/inappropriate-flags"
          element={
            <ProtectedRoute>
              <InappropriateFlags />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account-deletion-requests"
          element={
            <ProtectedRoute>
              <AccountDeletionRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reset-password"
          element={<ResetPassword />}
        />
        <Route 
          path="/confirm-email" 
          element={<EmailConfirmation />} 
        />
        <Route 
          path="/mobile-app-instructions" 
          element={<MobileAppInstructions />} 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

// Root App component with providers
const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
